import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import {
  Alert,
  Container,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  IconButton,
  Button,
} from '@mui/material';
import { ErrorOutline, PreviewOutlined, Download } from '@mui/icons-material';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import Select from 'antd/lib/select';

import FormDateWithStartAndEnd from 'retired/FormDateWithStartAndEnd';
import TabItem from 'retired/TabItem';
import { selectUser } from 'entities/user/selectors';
import { useInvoicesQuery, InvoiceStatus, DonationDisbursementType } from 'generated/graphql';
import { Privileges } from 'utils/privileges';
import { currencyFormat, CurrencyCode } from 'utils/currencyFormat';
import { renderInvoiceStatus } from 'utils/renderInvoiceStatus';

import { InvoiceDetails } from './InvoiceDetails';

const { Option } = Select;

const unprotectedStatuses = [InvoiceStatus.Paid, InvoiceStatus.Sent];

const InvoicesPage = (): JSX.Element => {
  const history = useHistory();
  const user = useSelector(selectUser);

  const invoicingEnabled = Object.values(
    user?.organization?.disbursementConfiguration?.invoicing || {}
  ).some((v) => v);
  const payrollInvoicingEnabled =
    user?.organization?.disbursementConfiguration?.invoicing?.[
      DonationDisbursementType.PayrollDonation
    ];

  const canViewInvoices =
    user && user.hasPrivilege(Privileges.RESTRICTED_INVOICING_VIEW) && invoicingEnabled;
  useEffect(() => {
    if (user && !canViewInvoices) {
      history.replace('/');
    }
  }, [user, canViewInvoices, history]);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [type, setType] = useState<DonationDisbursementType | ''>('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, loading, error } = useInvoicesQuery({
    variables: {
      take: rowsPerPage,
      skip: page * rowsPerPage,
      createdAfter: startDate ? startDate.toISOString() : undefined,
      createdBefore: endDate ? endDate.toISOString() : undefined,
      status: status || undefined,
      type: type || undefined,
    },
    skip: !canViewInvoices,
  });

  if (!user || !canViewInvoices) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Helmet title="Invoices" />
        <Box my={4}>
          <Alert icon={<ErrorOutline />} severity="error">
            There was an error loading invoices. Please try again.
          </Alert>
        </Box>
      </Container>
    );
  }

  const invoices = data?.invoices?.items || [];
  const totalCount = data?.invoices?.metadata?.totalItems || 0;
  const selectedInvoice = invoices.find((invoice) => invoice.id === selectedInvoiceId);

  if (selectedInvoice) {
    return <InvoiceDetails invoice={selectedInvoice} onClose={() => setSelectedInvoiceId(null)} />;
  }

  const dateFilter = (
    /* @ts-expect-error messed up types */
    <FormDateWithStartAndEnd
      names={['startDate', 'endDate']}
      startDate={{
        input: {
          value: startDate,
          onChange: (value: Date) => {
            setStartDate(value);
            setPage(0);
          },
        },
        meta: { touched: false, error: null },
      }}
      endDate={{
        input: {
          value: endDate,
          onChange: (value: Date) => {
            setEndDate(value);
            setPage(0);
          },
        },
        meta: { touched: false, error: null },
      }}
      allowEmpty={[true, true]}
      placeholder={['Created After', 'Created Before']}
    />
  );

  const accessibleStatuses = user.hasPrivilege(Privileges.RESTRICTED_INVOICING_MANAGE)
    ? Object.values(InvoiceStatus)
    : unprotectedStatuses;

  const typesFilter = (
    <Select
      value={type || undefined}
      placeholder="Filter by type"
      onChange={(newValue) => {
        setType(newValue);
        setPage(0);
      }}
      allowClear
      style={{
        width: 200,
      }}
    >
      {Object.values(DonationDisbursementType).map((t) => (
        <Option value={t} key={t}>
          {t}
        </Option>
      ))}
    </Select>
  );

  const statusFilter = (
    <Select
      value={status || undefined}
      placeholder="Filter by status"
      onChange={(newValue) => {
        setStatus(newValue);
        setPage(0);
      }}
      allowClear
      style={{
        width: 200,
      }}
    >
      {accessibleStatuses.map((s) => (
        <Option value={s} key={s}>
          {renderInvoiceStatus(s)}
        </Option>
      ))}
    </Select>
  );

  const table = (
    <>
      <TableContainer>
        <Table aria-label="invoices table">
          <TableHead>
            <TableRow>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Invoice Type</TableCell>
              <TableCell>Invoice Amount</TableCell>
              <TableCell>Issue Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>View PDF</TableCell>
              <TableCell>Disbursements</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No invoices found.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <Button variant="text" onClick={() => setSelectedInvoiceId(invoice.id)}>
                      {invoice.invoiceNumber}
                    </Button>
                  </TableCell>
                  <TableCell>{invoice.types.join(', ')}</TableCell>
                  <TableCell>
                    {currencyFormat(invoice.amount, invoice.currencyCode as CurrencyCode)}
                  </TableCell>
                  <TableCell>
                    {invoice.createdAt ? format(new Date(invoice.createdAt), 'MMM dd, yyyy') : '-'}
                  </TableCell>
                  <TableCell>{renderInvoiceStatus(invoice.status)}</TableCell>
                  <TableCell>
                    <IconButton
                      href={invoice.invoiceDocumentURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!invoice.invoiceDocumentURL}
                    >
                      <PreviewOutlined />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      href={invoice.disbursementsDocumentURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={!invoice.disbursementsDocumentURL}
                    >
                      <Download />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_event, newPage) => {
          setPage(newPage);
        }}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />
    </>
  );

  return (
    <>
      <Helmet title="Invoices" />
      <Box maxWidth={1280}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={4}>
          <Box>
            {user.hasPrivilege(Privileges.RESTRICTED_INVOICING_MANAGE) &&
              payrollInvoicingEnabled && (
                <TabItem onClick={() => history.push('/invoices/create')}>Create Invoice</TabItem>
              )}
            <TabItem isActive>Invoices</TabItem>
          </Box>
        </Box>

        <Box display="flex" gap={2}>
          {dateFilter}
          {typesFilter}
          {statusFilter}
        </Box>

        <Paper style={{ marginTop: 24 }}>{table}</Paper>
      </Box>
    </>
  );
};

export default InvoicesPage;
